import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import {
  getAuth,
  signInWithCustomToken,
  browserSessionPersistence,
  setPersistence,
} from "firebase/auth";
import { Lock, XCircle, Loader2 } from "lucide-react";

// ── How this works ────────────────────────────────────────────────────────
// The super admin clicks "Login as Admin" which opens this page in a NEW TAB.
//
// Problem: Firebase Auth uses localStorage by default — shared across all
// tabs — so signing in here would log out the super admin in the other tab.
//
// Solution: Set persistence to browserSessionPersistence (sessionStorage)
// BEFORE signing in. sessionStorage is tab-isolated, so:
//   • This new tab: signed in as admin  ✅
//   • Super admin tab: session untouched ✅
//
// Important: setPersistence MUST be called before signInWithCustomToken.

function AdminLogin() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | suspended | error

  useEffect(() => {
    const params    = new URLSearchParams(window.location.search);
    const token     = params.get("token");
    const companyId = params.get("companyId");

    if (!token) { navigate("/login"); return; }

    async function doLogin() {
      try {
        const auth = getAuth();

        // ── CRITICAL: Set session-only persistence BEFORE signing in ────
        // This stores the auth state in sessionStorage (tab-only),
        // NOT in localStorage (shared across tabs).
        // The super admin tab reads from localStorage → completely unaffected.
        await setPersistence(auth, browserSessionPersistence);

        // ── Sign in with the custom token ───────────────────────────────
        await signInWithCustomToken(auth, token);

        // ── Check company status (now authenticated, Firestore will allow read) ──
        if (companyId) {
          const companySnap = await getDoc(doc(db, "companies", companyId));

          if (companySnap.exists()) {
            const company = companySnap.data();

            // Block if company manually disabled
            if (company.active === false) {
              await auth.signOut();
              setStatus("suspended");
              return;
            }

            // Block if expiry-date plan and date has passed
            if (company.billing_plan === "expiry_date" && company.subscription_expiry) {
              const expiry = company.subscription_expiry.toDate
                ? company.subscription_expiry.toDate()
                : new Date(company.subscription_expiry);
              if (expiry < new Date()) {
                await auth.signOut();
                setStatus("suspended");
                return;
              }
            }

            // Block if monthly plan and balance is 0 or below
            if (
              company.billing_plan === "monthly" &&
              (company.billing_balance === 0 || company.billing_balance <= 0)
            ) {
              await auth.signOut();
              setStatus("suspended");
              return;
            }
          }
        }

        // ── All checks passed — go to admin dashboard ───────────────────
        navigate("/admin");

      } catch (err) {
        console.error("AdminLogin error:", err.message);
        setStatus("error");
        setTimeout(() => navigate("/login"), 3000);
      }
    }

    doLogin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Suspended screen ─────────────────────────────────────────────────────
  if (status === "suspended") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="bg-white rounded-xl shadow-sm p-10 text-center max-w-md w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Account Suspended</h2>
          <p className="text-gray-500 text-sm leading-relaxed">
            Your account has been suspended due to a payment issue or subscription expiry.
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Please contact support to restore access.
          </p>
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-400 mb-1">Need help? Contact us at:</p>
            <p className="text-sm font-semibold text-gray-700">support@pinpoint.ae</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Error screen ─────────────────────────────────────────────────────────
  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">Sign in failed</p>
          <p className="text-gray-400 text-sm mt-1">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // ── Loading screen ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <Loader2 className="w-12 h-12 text-blue-400 mx-auto mb-3 animate-spin" />
        <p className="text-gray-700 font-medium">Signing in to admin account...</p>
        <p className="text-gray-400 text-sm mt-1">Please wait</p>
      </div>
    </div>
  );
}

export default AdminLogin;
